import { useRef, useState, type JSX } from 'react'
import {
  publishHeadcountPlan,
  SessionExpiredError,
  type HeadcountPlanData,
  type PublishHeadcountInput,
} from '../api'
import { monthLabel, yen } from '../format'

// The headcount & AP plan editor. This is where the plan is authored now — it
// used to live on the '18 Month Headcount & AP Plan' sheet tab. Any
// FINANCE_VIEWER may edit and publish it (Paul 2026-08-12), including per-hire
// planned salaries.
//
// Publishing mints a NEW immutable version (the backend keeps every prior one),
// so there is no "unsaved edit" that can be lost silently and no in-place
// mutation — a correction is a new version, a rollback is re-publishing an old
// one. After publish we re-read the whole dashboard, because the plan feeds the
// GOSPEL forecast rows and the charts above.

// Rows carry a STABLE id (not the array index) so a mid-list removal doesn't
// make React reconcile the wrong DOM node — otherwise focus/caret jumps to
// whatever row now sits at the removed index.
type HireDraft = { id: number; role: string; annualSalary: string; benefitsRate: string; startMonth: string }
type OpexDraft = { id: number; category: string; monthlyAmount: string; startMonth: string }

// The <input type="month"> value is YYYY-MM; the API accepts that or -01.
const toMonthInput = (iso: string): string => iso.slice(0, 7)

export function HeadcountPlanEditor({
  plan,
  apiUrl,
  token,
  onPublished,
  onSessionExpired,
}: {
  plan: HeadcountPlanData | null
  apiUrl: string
  token: string
  onPublished: () => void
  onSessionExpired: (message: string) => void
}): JSX.Element {
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [baseline, setBaseline] = useState('')
  const [hires, setHires] = useState<HireDraft[]>([])
  const [opex, setOpex] = useState<OpexDraft[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const idRef = useRef(0)
  const newId = (): number => {
    idRef.current += 1
    return idRef.current
  }

  const startEdit = (): void => {
    setName('')
    setBaseline(plan?.baselineMonthly ?? '')
    setHires((plan?.hires ?? []).map((h) => ({
      id: newId(),
      role: h.role,
      annualSalary: h.annualSalary,
      benefitsRate: h.benefitsRate,
      startMonth: toMonthInput(h.startMonth),
    })))
    setOpex((plan?.opex ?? []).map((o) => ({
      id: newId(),
      category: o.category,
      monthlyAmount: o.monthlyAmount,
      startMonth: toMonthInput(o.startMonth),
    })))
    setError(null)
    setEditing(true)
  }

  const publish = async (): Promise<void> => {
    setSaving(true)
    setError(null)
    const input: PublishHeadcountInput = {
      name: name.trim() || undefined,
      baselineMonthly: baseline.trim() === '' ? null : baseline.trim(),
      hires: hires.map((h) => ({
        role: h.role.trim(),
        annualSalary: h.annualSalary.trim(),
        benefitsRate: h.benefitsRate.trim() || '0.15',
        startMonth: h.startMonth,
      })),
      opex: opex.map((o) => ({
        category: o.category.trim(),
        monthlyAmount: o.monthlyAmount.trim(),
        startMonth: o.startMonth,
      })),
    }
    try {
      await publishHeadcountPlan(apiUrl, token, input)
      setEditing(false)
      onPublished()
    } catch (cause) {
      // An expired session can't be recovered from inside the editor (there's no
      // sign-in affordance here), so hand it up to App to sign out to login.
      if (cause instanceof SessionExpiredError) {
        onSessionExpired(cause.message)
        return
      }
      // The backend validates and reports the offending field in the message.
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setSaving(false)
    }
  }

  const setHire = (index: number, patch: Partial<HireDraft>): void =>
    setHires((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  const setOpexRow = (index: number, patch: Partial<OpexDraft>): void =>
    setOpex((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))

  return (
    <section className="panel plan-editor">
      <header className="chart-head">
        <div>
          <h2>Headcount &amp; AP plan</h2>
          <p className="chart-sub">
            {plan
              ? <>Current version: <b>{plan.name}</b>, published {new Date(plan.capturedAt).toLocaleDateString()}. Publishing saves a new version — the old one is kept.</>
              : 'No plan published yet. Add hires and planned costs, then publish the first version.'}
          </p>
        </div>
        {!editing && (
          <button type="button" className="primary" onClick={startEdit}>
            {plan ? 'Edit plan' : 'Create plan'}
          </button>
        )}
      </header>

      {!editing && plan && <PlanReadView plan={plan} />}

      {editing && (
        <div className="plan-form">
          <label className="plan-name">
            Version label (optional)
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. FY26 Q3 plan" />
          </label>

          <h3>Planned hires</h3>
          <div className="plan-grid hires">
            <div className="plan-grid-head">
              <span>Role</span>
              <span>Annual salary</span>
              <span>Benefits rate</span>
              <span>Start month</span>
              <span />
            </div>
            {hires.map((hire, index) => (
              <div className="plan-grid-row" key={hire.id}>
                <input aria-label="Role" value={hire.role} onChange={(e) => setHire(index, { role: e.target.value })} placeholder="Role" />
                <input aria-label="Annual salary" value={hire.annualSalary} onChange={(e) => setHire(index, { annualSalary: e.target.value })} inputMode="decimal" placeholder="0" />
                <input aria-label="Benefits rate" value={hire.benefitsRate} onChange={(e) => setHire(index, { benefitsRate: e.target.value })} inputMode="decimal" placeholder="0.15" />
                <input aria-label="Start month" type="month" value={hire.startMonth} onChange={(e) => setHire(index, { startMonth: e.target.value })} />
                <button type="button" className="row-remove" aria-label="Remove hire" onClick={() => setHires((rows) => rows.filter((r) => r.id !== hire.id))}>
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="row-add"
              onClick={() => setHires((rows) => [...rows, { id: newId(), role: '', annualSalary: '', benefitsRate: '0.15', startMonth: '' }])}
            >
              + Add hire
            </button>
          </div>

          <h3>Planned opex</h3>
          <div className="plan-grid opex">
            <div className="plan-grid-head">
              <span>Category</span>
              <span>Monthly amount</span>
              <span>Start month</span>
              <span />
            </div>
            {opex.map((row, index) => (
              <div className="plan-grid-row" key={row.id}>
                <input aria-label="Opex category" value={row.category} onChange={(e) => setOpexRow(index, { category: e.target.value })} placeholder="Category" />
                <input aria-label="Monthly amount" value={row.monthlyAmount} onChange={(e) => setOpexRow(index, { monthlyAmount: e.target.value })} inputMode="decimal" placeholder="0" />
                <input aria-label="Start month" type="month" value={row.startMonth} onChange={(e) => setOpexRow(index, { startMonth: e.target.value })} />
                <button type="button" className="row-remove" aria-label="Remove opex line" onClick={() => setOpex((rows) => rows.filter((r) => r.id !== row.id))}>
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="row-add"
              onClick={() => setOpex((rows) => [...rows, { id: newId(), category: '', monthlyAmount: '', startMonth: '' }])}
            >
              + Add opex line
            </button>
          </div>

          <label className="plan-name">
            Baseline annualized cost, monthly (optional)
            <input value={baseline} onChange={(e) => setBaseline(e.target.value)} inputMode="decimal" placeholder="leave blank for none" />
          </label>

          {error !== null && (
            <p className="error" role="alert">
              {error}
            </p>
          )}

          <div className="plan-actions">
            <button type="button" className="primary" onClick={() => void publish()} disabled={saving}>
              {saving ? 'Publishing…' : 'Publish new version'}
            </button>
            <button type="button" onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function PlanReadView({ plan }: { plan: HeadcountPlanData }): JSX.Element {
  return (
    <div className="plan-read">
      <div className="scroller">
        <table>
          <thead>
            <tr>
              <th scope="col">Role</th>
              <th scope="col">Annual salary</th>
              <th scope="col">Benefits</th>
              <th scope="col">Starts</th>
            </tr>
          </thead>
          <tbody>
            {plan.hires.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted">No planned hires.</td>
              </tr>
            ) : (
              plan.hires.map((hire, index) => (
                <tr key={`h-${index}`}>
                  <th scope="row" className="rowhead">{hire.role}</th>
                  <td>{yen(hire.annualSalary)}</td>
                  <td>{(Number(hire.benefitsRate) * 100).toFixed(1)}%</td>
                  <td>{monthLabel(hire.startMonth)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(plan.opex.length > 0 || plan.baselineMonthly !== null) && (
        <div className="scroller">
          <table>
            <thead>
              <tr>
                <th scope="col">Opex</th>
                <th scope="col">Monthly</th>
                <th scope="col">Starts</th>
              </tr>
            </thead>
            <tbody>
              {plan.opex.map((row, index) => (
                <tr key={`o-${index}`}>
                  <th scope="row" className="rowhead">{row.category}</th>
                  <td>{yen(row.monthlyAmount)}</td>
                  <td>{monthLabel(row.startMonth)}</td>
                </tr>
              ))}
              {plan.baselineMonthly !== null && (
                <tr>
                  <th scope="row" className="rowhead">Baseline (annualized, monthly)</th>
                  <td>{yen(plan.baselineMonthly)}</td>
                  <td className="muted">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
